/**
 * Spec 088c — ScoreBadge / ScoreCard design-system registry.
 *
 * This module is the SINGLE SOURCE OF TRUTH for how a score is rendered
 * across the GEO Observatory SPA. Downstream specs (086/087) add three new scores
 * alongside the three existing ones; keeping the mapping here prevents
 * each page from re-inventing its own color/icon scheme.
 *
 * ENFORCEMENT: `ScoreType` is a closed string-literal union. `ScoreBadge`
 * requires a `scoreType: ScoreType` prop, so any consumer passing a
 * non-registered literal (e.g. `"NotARealScore"`) is a compile-time error.
 * The discriminated-union design is asserted by Gate 088c.B.
 */
/**
 * Closed set of score identifiers. Adding a new score means:
 *   1. Add the literal to this union,
 *   2. Add the matching entry to SCORE_DESIGN,
 *   3. The type-checker will flag any missing `SCORE_DESIGN[...]` entry at
 *      compile time (exhaustiveness via `Record<ScoreType, ScoreDesign>`).
 */
export type ScoreType =
  | 'VisibilityScore'
  | 'GapScore'
  | 'DomainAuthority'
  | 'CoverageScore'
  | 'QualityScore'
  | 'TopicDistributionScore';

export type ScoreCategory = 'visibility' | 'authority' | 'coverage';

export type ScoreTone = 'ok' | 'info' | 'warn' | 'fail';

export interface ScoreDesign {
  /** Spec 230 — restyled onto `.hub-chip`'s four tones. Most scores are a
   * neutral tracked metric (`info`); `GapScore` is the one score that is
   * itself a call-out ("visibility gaps"), so it takes `warn`. */
  tone: ScoreTone;
  /** Boxicon name shown to the left of the value (e.g. 'bx-show'). Spec 230. */
  icon: string;
  /** Human-readable tooltip explaining the metric. */
  tooltip: string;
  /** Grouping for score cards (e.g. ScoreCard triptych). */
  category: ScoreCategory;
  /** Display label (short, sentence-cased). */
  label: string;
}

export const SCORE_DESIGN: Record<ScoreType, ScoreDesign> = {
  VisibilityScore: {
    tone: 'info',
    icon: 'bx-show',
    tooltip:
      'How often your brand appears in AI-generated responses across tracked prompts.',
    category: 'visibility',
    label: 'Visibility',
  },
  GapScore: {
    tone: 'warn',
    icon: 'bx-error',
    tooltip:
      'Severity of visibility gaps — engines where the brand is absent, misrepresented, or out-cited.',
    category: 'visibility',
    label: 'Gap',
  },
  DomainAuthority: {
    tone: 'info',
    icon: 'bx-shield',
    tooltip:
      'External authority signal for a cited domain (Moz/Ahrefs-style DA surrogate).',
    category: 'authority',
    label: 'Domain authority',
  },
  CoverageScore: {
    tone: 'info',
    icon: 'bx-layer',
    tooltip:
      'Share of a brand’s sitemap that has been evaluated against GEO rules.',
    category: 'coverage',
    label: 'Coverage',
  },
  QualityScore: {
    tone: 'ok',
    icon: 'bx-star',
    tooltip: 'Aggregate G-Eval quality of the evaluated pages.',
    category: 'coverage',
    label: 'Quality',
  },
  TopicDistributionScore: {
    tone: 'info',
    icon: 'bx-pie-chart',
    tooltip:
      'How evenly a brand’s evaluated content spans the observed topic clusters.',
    category: 'coverage',
    label: 'Topic distribution',
  },
};

/** All score types, in stable display order. Useful for mapping UIs. */
export const SCORE_TYPES_IN_ORDER: readonly ScoreType[] = [
  'VisibilityScore',
  'GapScore',
  'DomainAuthority',
  'CoverageScore',
  'QualityScore',
  'TopicDistributionScore',
] as const;

/** Coverage-family triptych order (used by ScoreCard). */
export const COVERAGE_SCORES_IN_ORDER = [
  'CoverageScore',
  'QualityScore',
  'TopicDistributionScore',
] as const satisfies readonly ScoreType[];
